<script setup lang="ts">

import AppModal from "@/components/appModal.vue";

/**
 * Remove group confirmation modal
 *
 * Shared confirmation modal used wherever a groove or a stone group can be removed
 * (itemV3Grooves.vue, itemV3StonesList.vue, itemV3Stones.vue). The parent component
 * keeps track of which group/groove is pending removal and only decides what to do
 * once the "confirm" event is emitted - this component only owns the visibility
 * (v-model) and the confirm/cancel UI.
 */

const props = withDefaults(defineProps<{
  modelValue: boolean;
  itemName?: string;
}>(), {
  itemName: "",
});

const emit = defineEmits<{
  (event: "update:modelValue", value: boolean): void;
  (event: "confirm"): void;
}>();

const close = () => emit("update:modelValue", false);

const handleConfirm = () => {
  emit("confirm");
  close();
};

</script>

<template>
  <app-modal :model-value="props.modelValue" @update:model-value="emit('update:modelValue', $event)"
             :label="$t('components.itemV2.itemV2Main.remove_feature_confirm_title')">
    <transition name="fade-fast">
      <div v-if="props.modelValue"
           class="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900/40 backdrop-blur-sm p-6"
           @click="close">
        <div class="w-full max-w-sm rounded-2xl border border-white/70 bg-parameter-box-bg p-8 shadow-[0_1px_3px_rgba(68,68,68,0.06)] animate-scale-in"
             @click.stop>
          <div class="mb-6 flex items-center justify-center">
            <div class="flex size-16 items-center justify-center rounded-full bg-brand-main/10 text-brand-main">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
            </div>
          </div>
          <h3 class="m-0 mb-2 text-center text-sm tracking-tight text-gray-900 font-ivymode">
            {{ $t('components.itemV2.itemV2Main.remove_feature_confirm_title') }}</h3>
          <p class="m-0 text-center text-[10px] leading-[15px] font-normal tracking-[0.1px] text-parameter-text"
             :class="itemName ? 'mb-2' : 'mb-8'">
            {{ $t('components.itemV2.itemV2Main.remove_feature_confirm_text') }}</p>
          <p v-if="itemName" class="m-0 mb-8 text-center text-xs font-medium text-parameter-title">
            {{ itemName }}</p>
          <div class="flex flex-col gap-3">
            <button
                type="button"
                @click="handleConfirm"
                class="w-full cursor-pointer rounded-2xl bg-brand-main py-3.5 text-xs font-medium text-white transition-all hover:bg-brand-main/90 active:scale-95"
            >
              {{ $t('components.itemV2.itemV2ItemWidget.confirm') }}
            </button>
            <button
                type="button"
                @click="close"
                class="w-full cursor-pointer rounded-2xl bg-[#f3f0e9] py-3.5 text-xs font-medium text-parameter-text transition-all hover:bg-[#e7ddcc] hover:text-parameter-title active:scale-95"
            >
              {{ $t('components.itemV2.itemV2ItemWidget.cancel') }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </app-modal>
</template>

<style scoped>
.fade-fast-enter-active,
.fade-fast-leave-active {
  transition: opacity 0.15s ease;
}

.fade-fast-enter-from,
.fade-fast-leave-to {
  opacity: 0;
}

@keyframes scale-in {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.animate-scale-in {
  animation: scale-in 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
</style>
